/**
 * MiniDaw Component
 * Complete, standalone, plug-and-play MIDI recorder & piano roll editor for Chord Notator
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  MiniDawProps,
  MidiDeviceItem,
  MidiExportData,
  DetectedChord,
  SplitLayoutConfig,
} from '../types/midi';
import { useMidiRecorder } from '../hooks/useMidiRecorder';
import { usePianoRollEditor } from '../hooks/usePianoRollEditor';
import { TransportBar } from './TransportBar';
import { PianoRoll } from './PianoRoll';
import { VelocityLane } from './VelocityLane';
import { VirtualKeyboard } from './VirtualKeyboard';
import { PianoScoreView } from './PianoScoreView';
import { DawSplitWorkspace } from './DawSplitWorkspace';
import { ExportModal } from './ExportModal';
import { MidiSettingsModal } from './MidiSettingsModal';
import { getTicksPerMeasure } from '../core/timing';
import { detectAllMeasureChords } from '../core/chordDetector';

export const MiniDaw: React.FC<MiniDawProps> = ({
  bpm: propBpm = 120,
  timeSignature: propTimeSignature = { beats: 4, beatType: 4 },
  onChange,
  midiInput = null,
  audioContext = null,
  initialNotes = [],
  initialMeasures = 16,
  layoutConfig: propLayoutConfig,
  onLayoutConfigChange: propOnLayoutConfigChange,
  displayOptions,
  onUndoRedoChange,
  isFullScreenScoreOpen,
  onToggleFullScreenScore,
  renderChartPane,
  onSendChordsToChart,
  className = '',
}) => {
  // Recorder Hook
  const recorder = useMidiRecorder({
    bpm: propBpm,
    timeSignature: propTimeSignature,
    initialNotes,
    externalAudioContext: audioContext,
    externalMidiInput: midiInput,
    onChange,
  });

  // Editor Hook
  const editor = usePianoRollEditor({
    notes: recorder.rawNotes,
    onChangeNotes: recorder.commitNotesChange,
    audioEngine: recorder.audioEngine,
    grid: recorder.quantizeOptions.grid,
    ppq: recorder.ppq,
  });

  // Modal states
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isMidiSettingsOpen, setIsMidiSettingsOpen] = useState(false);

  // MIDI Devices list
  const [devices, setDevices] = useState<MidiDeviceItem[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [isMidiSupported, setIsMidiSupported] = useState<boolean>(true);

  // Keyboard & Velocity Lane Resizing states
  const [keyboardWidth, setKeyboardWidth] = useState<number>(76);
  const [velocityLaneHeight, setVelocityLaneHeight] = useState<number>(84);
  const [showVelocityLane, setShowVelocityLane] = useState<boolean>(false);
  const [editorScrollLeft, setEditorScrollLeft] = useState<number>(0);

  // Playhead Auto-Scroll across all views (Piano Roll, Score, Lead Sheet)
  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  // Multi-Display Split View Configuration & Virtual Keyboard Visibility
  // Default to vertical split when multiple panes are active; virtual keyboard hidden by default
  const [internalLayoutConfig, setInternalLayoutConfig] = useState<SplitLayoutConfig>({
    paneCount: 1,
    direction: 'vertical',
    panes: ['pianoroll', 'score', 'chart'],
    splitRatios: [100],
  });
  const layoutConfig = propLayoutConfig ?? internalLayoutConfig;
  const setLayoutConfig = useCallback(
    (action: React.SetStateAction<SplitLayoutConfig>) => {
      const nextConfig =
        typeof action === 'function' ? (action as (prev: SplitLayoutConfig) => SplitLayoutConfig)(layoutConfig) : action;
      if (propOnLayoutConfigChange) {
        propOnLayoutConfigChange(nextConfig);
      } else {
        setInternalLayoutConfig(nextConfig);
      }
    },
    [layoutConfig, propOnLayoutConfigChange]
  );
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState<boolean>(false);

  // Initialize MIDI Access
  useEffect(() => {
    setIsMidiSupported(recorder.midiAccess.isSupported());
    recorder.midiAccess.initialize().then(() => {
      setDevices(recorder.midiAccess.getInputs());
      setActiveDeviceId(recorder.midiAccess.getActiveInputId());
    });

    const unsubscribe = recorder.midiAccess.onDeviceChange((devs, activeId) => {
      setDevices(devs);
      setActiveDeviceId(activeId);
    });

    return unsubscribe;
  }, [recorder.midiAccess]);

  // Handle refresh
  const handleRefreshMidi = useCallback(() => {
    recorder.midiAccess.initialize().then(() => {
      setDevices(recorder.midiAccess.getInputs());
      setActiveDeviceId(recorder.midiAccess.getActiveInputId());
    });
  }, [recorder.midiAccess]);

  const measureTicks = getTicksPerMeasure(recorder.timeSignature, recorder.ppq);
  const maxEndTick = recorder.quantizedNotes.reduce(
    (max, n) => Math.max(max, n.startTick + n.durationTicks),
    0
  );
  const totalMeasures = Math.max(initialMeasures, Math.ceil(maxEndTick / measureTicks) + 4);
  const totalTicks = totalMeasures * measureTicks;

  // Manual Chord Overrides Map
  const [customChords, setCustomChords] = useState<Map<number, DetectedChord[]>>(new Map());

  // Auto harmonic analysis across all recorded notes, merged with manual chord edits
  const measureChords = useMemo<Map<number, DetectedChord[]>>(() => {
    const chords = detectAllMeasureChords(
      recorder.quantizedNotes,
      recorder.timeSignature,
      recorder.ppq,
      totalMeasures
    );
    customChords.forEach((val, key) => {
      chords.set(key, val);
    });
    return chords;
  }, [recorder.quantizedNotes, recorder.timeSignature, recorder.ppq, totalMeasures, customChords]);

  // Automatically push detected chords (block or arpeggio) into the host's chart as they're
  // played — non-destructive (host-side guard never overwrites a measure already written by
  // hand). Debounced so it doesn't fire on every single tick while notes are being placed.
  useEffect(() => {
    if (!onSendChordsToChart || measureChords.size === 0) return;
    const timer = setTimeout(() => {
      onSendChordsToChart(measureChords);
    }, 500);
    return () => clearTimeout(timer);
  }, [measureChords, onSendChordsToChart]);

  const handleUpdateChord = useCallback((measureIndex: number, newSymbol: string) => {
    setCustomChords((prev) => {
      const next = new Map(prev);
      const clean = newSymbol.trim();
      if (!clean) {
        next.delete(measureIndex);
      } else {
        // Can parse multiple chords separated by space or hyphen (e.g. "F C" or "F - C")
        const tokens = clean.split(/[\s,-]+/).filter(Boolean);
        const mStartTick = measureIndex * measureTicks;
        const sliceTicks = Math.floor(measureTicks / Math.max(1, tokens.length));

        const parsedChords: DetectedChord[] = tokens.map((token, idx) => {
          const rootMatch = token.match(/^[A-G][#b]?/);
          const root = rootMatch ? rootMatch[0] : token;
          const remainder = token.slice(root.length);
          const [suffix, bass] = remainder.includes('/') ? remainder.split('/') : [remainder, undefined];
          return {
            symbol: token,
            root,
            suffix: suffix || '',
            bass: bass || root,
            startTick: mStartTick + idx * sliceTicks,
            durationTicks: sliceTicks,
            confidence: 1.0,
          };
        });

        next.set(measureIndex, parsedChords);
      }
      return next;
    });
  }, [measureTicks]);

  // Build exact ExportData for modal and host callback
  const exportData: MidiExportData = useMemo(() => {
    return {
      ppq: recorder.ppq,
      bpm: recorder.bpm,
      timeSignature: recorder.timeSignature,
      quantizeGrid: recorder.quantizeOptions.grid,
      notes: recorder.quantizedNotes.map((n) => ({
        pitch: n.pitch,
        velocity: n.velocity,
        startTick: n.startTick,
        durationTicks: n.durationTicks,
        channel: n.channel,
      })),
      chords: Array.from(measureChords.entries()).flatMap(([measureIndex, chords]) =>
        chords.map((chord) => ({
          measureIndex,
          chord,
        }))
      ),
    };
  }, [
    recorder.ppq,
    recorder.bpm,
    recorder.timeSignature,
    recorder.quantizeOptions.grid,
    recorder.quantizedNotes,
    measureChords,
  ]);

  // Sync Undo/Redo state to host/parent header
  useEffect(() => {
    onUndoRedoChange?.({
      canUndo: recorder.canUndo,
      canRedo: recorder.canRedo,
      undo: recorder.undo,
      redo: recorder.redo,
    });
  }, [recorder.canUndo, recorder.canRedo, recorder.undo, recorder.redo, onUndoRedoChange]);

  // Spacebar Play / Stop shortcut
  const recorderRef = useRef(recorder);
  recorderRef.current = recorder;

  useEffect(() => {
    const handleGlobalShortcuts = (e: KeyboardEvent) => {
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') return;

      const rec = recorderRef.current;

      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ' && !e.shiftKey) {
        e.preventDefault();
        rec.undo();
      } else if (
        ((e.ctrlKey || e.metaKey) && e.code === 'KeyY') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyZ')
      ) {
        e.preventDefault();
        rec.redo();
      } else if (e.code === 'Space') {
        e.preventDefault();
        if (rec.transportState === 'playing' || rec.transportState === 'recording' || rec.transportState === 'count-in') {
          rec.stop();
        } else {
          rec.play();
        }
      } else if (e.code === 'KeyM' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        rec.setMetronomeEnabled(!rec.metronomeEnabled);
      } else if (e.code === 'Comma' || (e.code === 'ArrowLeft' && e.altKey)) {
        e.preventDefault();
        rec.rewind();
      } else if (e.code === 'Period' || (e.code === 'ArrowRight' && e.altKey)) {
        e.preventDefault();
        rec.fastForward();
      } else if (e.code === 'Digit0' || e.code === 'Home') {
        e.preventDefault();
        rec.returnToStart();
      }
    };

    window.addEventListener('keydown', handleGlobalShortcuts);
    return () => window.removeEventListener('keydown', handleGlobalShortcuts);
  }, []);

  return (
    <div
      id="mini-daw-root"
      className={`flex flex-col h-full bg-[#F8F7F4] text-[#1B1B1B] font-sans border-2 border-[#1B1B1B] overflow-hidden shadow-sm ${className}`}
    >
      {/* Top Transport & Tool Bar */}
      <TransportBar
        audioEngine={recorder.audioEngine}
        transportState={recorder.transportState}
        position={recorder.currentPosition}
        bpm={recorder.bpm}
        onBpmChange={recorder.setBpm}
        timeSignature={recorder.timeSignature}
        onTimeSignatureChange={recorder.setTimeSignature}
        onPlay={recorder.play}
        onStop={recorder.stop}
        onRecord={recorder.record}
        onRewind={recorder.rewind}
        onFastForward={recorder.fastForward}
        onReturnToStart={recorder.returnToStart}
        loopEnabled={recorder.loopRange.enabled}
        onToggleLoop={() =>
          recorder.setLoopRange((prev) => {
            const nextEnabled = !prev.enabled;
            if (nextEnabled) {
              const measureTicks = getTicksPerMeasure(recorder.timeSignature, recorder.ppq);
              const loopStartTick = (prev.startMeasure - 1) * measureTicks;
              recorder.seekToTick(loopStartTick);
            }
            return { ...prev, enabled: nextEnabled };
          })
        }
        loopRange={recorder.loopRange}
        onUpdateLoopRange={recorder.setLoopRange}
        metronomeEnabled={recorder.metronomeEnabled}
        onToggleMetronome={() => recorder.setMetronomeEnabled(!recorder.metronomeEnabled)}
        countInMeasures={recorder.countInMeasures}
        onCountInChange={recorder.setCountInMeasures}
        countInBeat={recorder.countInBeat}
        quantizeOptions={recorder.quantizeOptions}
        onQuantizeChange={recorder.setQuantizeOptions}
        canUndo={recorder.canUndo}
        canRedo={recorder.canRedo}
        onUndo={recorder.undo}
        onRedo={recorder.redo}
        onOpenExport={() => setIsExportOpen(true)}
        onOpenMidiSettings={() => setIsMidiSettingsOpen(true)}
        midiDeviceName={recorder.midiAccess.getActiveInputName()}
        midiSupported={isMidiSupported}
        lastMidiActivity={recorder.lastMidiActivity}
        activeLivePitchesCount={recorder.livePitches.size}
        viewMode={layoutConfig.panes[0] || 'pianoroll'}
        onViewModeChange={(mode) =>
          setLayoutConfig((prev) => ({
            ...prev,
            paneCount: 1,
            panes: [mode, ...prev.panes.filter((p) => p !== mode)],
            splitRatios: [100],
          }))
        }
        layoutConfig={layoutConfig}
        onLayoutConfigChange={setLayoutConfig}
        showVirtualKeyboard={showVirtualKeyboard}
        onToggleVirtualKeyboard={() => setShowVirtualKeyboard((v) => !v)}
        showVelocityLane={showVelocityLane}
        onToggleVelocityLane={() => setShowVelocityLane((v) => !v)}
        autoScroll={autoScroll}
        onToggleAutoScroll={() => setAutoScroll((v) => !v)}
        showLoopControls={displayOptions?.showLoopControls ?? false}
        showPositionDisplay={displayOptions?.showPositionDisplay ?? false}
      />

      {/* Multi-Display Split Workspace (1, 2, or 3 panes; Horizontal / Vertical split) */}
      <DawSplitWorkspace
        layoutConfig={layoutConfig}
        onLayoutConfigChange={setLayoutConfig}
        showVirtualKeyboard={showVirtualKeyboard}
        onToggleVirtualKeyboard={() => setShowVirtualKeyboard((v) => !v)}
        showVelocityLane={showVelocityLane}
        onToggleVelocityLane={() => setShowVelocityLane((v) => !v)}
        renderPianoRoll={() => (
          <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden relative">
            {/* Main Piano Roll with Integrated Chord Track */}
            <PianoRoll
              notes={recorder.quantizedNotes}
              selectedNoteIds={editor.selectedIds}
              onSelectNote={editor.selectNote}
              onClearSelection={editor.clearSelection}
              onSelectAll={editor.selectAll}
              onAddNote={(pitch, tick, duration) => editor.addNote(pitch, tick, duration)}
              onUpdateNotesLive={recorder.updateRawNotesLive}
              onCommitNotes={recorder.commitNotesChange}
              onMoveNotes={editor.moveNotes}
              onResizeNotes={editor.resizeNotes}
              onDeleteSelected={editor.deleteSelected}
              onDeleteNote={editor.deleteNote}
              onSplitNote={editor.splitNoteAt}
              currentTick={recorder.currentTick}
              onSeek={recorder.seekToTick}
              bpm={recorder.bpm}
              timeSignature={recorder.timeSignature}
              ppq={recorder.ppq}
              grid={recorder.quantizeOptions.grid}
              loopRange={recorder.loopRange}
              onUpdateLoopRange={recorder.setLoopRange}
              audioEngine={recorder.audioEngine}
              isPlaying={recorder.transportState === 'playing'}
              autoScroll={autoScroll}
              onToggleAutoScroll={() => setAutoScroll((v) => !v)}
              showVelocityLane={showVelocityLane}
              onToggleVelocityLane={() => setShowVelocityLane((v) => !v)}
              showVirtualKeyboard={showVirtualKeyboard}
              onToggleVirtualKeyboard={() => setShowVirtualKeyboard((v) => !v)}
              livePitches={recorder.livePitches}
              totalMeasures={totalMeasures}
              keyboardWidth={keyboardWidth}
              onKeyboardWidthChange={setKeyboardWidth}
              measureChords={measureChords}
              onUpdateChord={handleUpdateChord}
              onScrollPositionChange={setEditorScrollLeft}
            />

            {/* Velocity Lane (hidden by default, toggled via toolbar or popover) */}
            {showVelocityLane && (
              <VelocityLane
                notes={recorder.quantizedNotes}
                selectedNoteIds={editor.selectedIds}
                onSelectNote={editor.selectNote}
                onSetNoteVelocity={editor.setNoteVelocity}
                pixelsPerQuarter={64}
                ppq={recorder.ppq}
                totalTicks={totalTicks}
                keyboardWidth={keyboardWidth}
                height={velocityLaneHeight}
                onHeightChange={setVelocityLaneHeight}
                scrollLeft={editorScrollLeft}
                onScrollLeft={setEditorScrollLeft}
              />
            )}
          </div>
        )}
        renderScore={() => (
          /* Piano Score / Partition View with Chord Badges, Page Turning, and Full Screen Mode */
          <PianoScoreView
            notes={recorder.quantizedNotes}
            timeSignature={recorder.timeSignature}
            ppq={recorder.ppq}
            bpm={recorder.bpm}
            currentTick={recorder.currentTick}
            onSeek={recorder.seekToTick}
            measureChords={measureChords}
            isPlaying={recorder.transportState === 'playing'}
            onPlay={recorder.play}
            onStop={recorder.stop}
            autoScroll={autoScroll}
            onToggleAutoScroll={() => setAutoScroll((v) => !v)}
            isFullScreenExternal={isFullScreenScoreOpen}
            onToggleFullScreenExternal={onToggleFullScreenScore}
            className="flex-1 min-h-0"
          />
        )}
        renderChart={() => (
          <div className="flex flex-col h-full min-h-0">
            <div className="shrink-0 px-3 py-1.5 bg-[#1B1B1B] text-white text-[11px] opacity-70">
              Lecture seule — modifiez les accords dans l'Edit Mode. Les accords joués sont envoyés automatiquement vers les mesures vides (rien n'est jamais remplacé ici).
            </div>
            <div className="flex-1 min-h-0">
              {renderChartPane ? (
                renderChartPane()
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-neutral-500 bg-[#F8F7F4]">
                  No chart available
                </div>
              )}
            </div>
          </div>
        )}
      />

      {/* Proportional Virtual Keyboard (Collapsible/hidden by default to maximize view area) */}
      {showVirtualKeyboard && <VirtualKeyboard onClose={() => setShowVirtualKeyboard(false)} />}

      {/* Export & Integration Schema Modal */}
      <ExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        exportData={exportData}
      />

      {/* MIDI Devices & Access Modal */}
      <MidiSettingsModal
        isOpen={isMidiSettingsOpen}
        onClose={() => setIsMidiSettingsOpen(false)}
        devices={devices}
        activeDeviceId={activeDeviceId}
        onSelectDevice={(id) => recorder.midiAccess.selectInput(id)}
        isSupported={isMidiSupported}
        onRefresh={handleRefreshMidi}
        audioEngine={recorder.audioEngine}
        lastMidiActivity={recorder.lastMidiActivity}
      />
    </div>
  );
};
